import React, { useState, useEffect } from "react";
import InputMask from "react-input-mask";
import {
  TextField,
  Autocomplete,
  Button,
  Typography,
  Container,
  Paper,
  MenuItem,
  Box,
  CircularProgress,
  Select,
  FormControlLabel,
  Checkbox,
  Link,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import Navbar from "../../../../globals/components/Navbar";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { apiClient } from "../../../../api/httpClient";
import { uploadGIA, uploadLOA } from "../../../../api/bondsClient";
import { toast } from "react-toastify";
import debounce from "lodash.debounce";
import { EMAIL_DEBOUNCE_TIMEOUT } from "../../../../globals/utils/constants";
import {
  resolveLawFirmUserResetSelection,
  resolveLawFirmUserSelection,
} from "./editLawFirmUserUtils";

const positions = ["Paralegal", "Attorney", "Clerical Staff"];
const suffixOptions = ["Jr.", "Sr.", "I", "II", "III", "IV", "V"];

function EditLawfirmUserDetailsForm() {
  const { id, location_id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(null);
  const [initialData, setInitialData] = useState(null);
  const [lawSchools, setLawSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [locations, setLocations] = useState([]);
  const [responseGlobal, setResponseGlobal] = useState(null);
  const [loadingResend, setLoadingResend] = useState(false);
  const [giaFile, setGiaFile] = useState(null);
  const [loaFile, setLoaFile] = useState(null);
  const [limits, setLimits] = useState({
    singleLimit: 0,
    aggregateLimit: 0,
    loading: true,
    error: null,
  });
  // New state for law firm selection
  const [lawFirms, setLawFirms] = useState([]);
  const [selectedLawFirm, setSelectedLawFirm] = useState("");
  const [loadingLawFirms, setLoadingLawFirms] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  
  // Email existence check state
  const [emailExists, setEmailExists] = useState(false);
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);

  const { role } = useSelector((state) => state.auth);

  const formatDateTime = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString();
  };

  // Debounced function to check email existence
  const debouncedCheckEmail = React.useRef(
    debounce(async (email) => {
      if (!email) {
        setEmailExists(false);
        setEmailChecking(false);
        setEmailDialogOpen(false);
        return;
      }
      setEmailChecking(true);
      try {
        await apiClient("/email-exists", "POST", { email });
        setEmailExists(false);
      } catch (e) {
        if (e?.detail === "Email already exists") {
          setEmailDialogOpen(true);
          setEmailExists(true);
        } else {
          setEmailExists(false);
        }
      } finally {
        setEmailChecking(false);
      }
    }, EMAIL_DEBOUNCE_TIMEOUT)
  ).current;

  // New functions for law firm and location management
  const fetchLawFirms = async () => {
    setLoadingLawFirms(true);
    try {
      const response = await apiClient("/list-lawfirms/", "GET");
      const formattedLawFirms = response.map((firm) => ({
        label: firm.name,
        value: firm.lawfirm_id,
      }));
      setLawFirms(formattedLawFirms);
    } catch (error) {
      console.error("Error fetching law firms:", error);
      toast.error("Failed to load law firms");
    } finally {
      setLoadingLawFirms(false);
    }
  };

  const fetchLocationsByLawFirm = async (lawFirmId) => {
    setLoadingLocations(true);
    try {
      const response = await apiClient(
        `/get-locations-by-lawfirm/${lawFirmId}`,
        "GET"
      );
      const formattedLocations = response.map((location) => ({
        label: location.name + " - " + location.address,
        value: location.law_firm_location_id,
        address: location.address,
      }));
      setLocations(formattedLocations);
      
      // Auto-select first location if only one exists
      if (formattedLocations.length === 1) {
        setSelectedLocation(formattedLocations[0].value);
      } else if (formattedLocations.length > 1) {
        // Auto-select main location if it exists
        const mainLocation = formattedLocations.find(
          (location) => location.label.includes("Main Location")
        );
        if (mainLocation) {
          setSelectedLocation(mainLocation.value);
        } else {
          setSelectedLocation(""); // Reset selection
        }
      } else {
        setSelectedLocation(""); // Reset selection
      }
    } catch (error) {
      console.error("Error fetching law firm locations:", error);
      toast.error("Failed to load locations for selected law firm");
    } finally {
      setLoadingLocations(false);
    }
  };

  // Dialog handlers
  const handleDialogClose = () => {
    setEmailDialogOpen(false);
  };
  const handleDialogContinue = () => {
    setEmailDialogOpen(false);
    setEmailExists(false);
    // Keep the email as is, allow user to proceed
  };
  const handleDialogCancel = () => {
    setEmailDialogOpen(false);
    setFormData((prev) => ({ ...prev, email: "" }));
    setEmailExists(false);
  };

  useEffect(() => {
    const fetchLawFirmUserDetails = async () => {
      try {
        setLoading(true);

        // Fetch all law firms first
        await fetchLawFirms();

        // Fetch user details
        const response = await apiClient(`/lawfirm-user/${id}/${location_id}`, "GET");
        setResponseGlobal(response);

        const { lawFirmId: currentLawFirmId, locationId } =
          resolveLawFirmUserSelection(response, location_id);

        setSelectedLawFirm(currentLawFirmId);

        // Fetch locations for the current law firm
        if (currentLawFirmId) {
          await fetchLocationsByLawFirm(currentLawFirmId);
        }

        const userDetails = {
          email: response.user.email,
          firstName: response.user.first_name,
          lastName: response.user.last_name,
          title: response.title,
          position: response.position,
          middleName: response.middle_name,
          suffix: response.suffix,
          prefix: response.prefix,
          preferredName: response.preferred_name,
          extension: response.extension,
          phoneNumber: response.phone_number || "",
          isDefault: response.is_default,
          isLegalService: response.is_legal_service,
          isActive: response.is_active,
          isAdmin: response.is_admin,
          lawSchool: response.lawschool,
          barNumber: response.bar || "",
          first_name: response.first_name,
          last_name: response.last_name,
          is_gia_on_file: response.is_gia_on_file || false,
          gia_document_link: response.gia_document_link || "",
          gia_document_name: response.gia_document_name || "",
          is_loa_on_file: response.is_loa_on_file || false,
          loa_document_link: response.loa_document_link || "",
          loa_document_name: response.loa_document_name || "",
          total_bonds: response.total_bonds || 0,
          total_bond_amount: response.total_outstanding_bond_amount || 0,
          firmEmail: response.firm_email || "",
          useFirmEmail: Boolean(response.firm_email),
        };
        setFormData(userDetails);
        setInitialData(userDetails);
        
        setSelectedLocation(locationId);

        // Fetch law schools
        const lawSchoolsResponse = await apiClient(`/lawschools/`, "GET");
        setLawSchools(lawSchoolsResponse.data);

        // Only fetch limits if user is an Attorney
        if (response.position === "Attorney") {
          // Fetch the most recent limits
          const limitsResponse = await apiClient(
            `/lawfirm-user/${id}/limits/latest/`,
            "GET"
          );
          setLimits({
            singleLimit: limitsResponse.line_of_business_single_limit || 0,
            aggregateLimit:
              limitsResponse.line_of_business_aggregate_limit || 0,
            loading: false,
            error: null,
          });
        } else {
          setLimits({
            singleLimit: 0,
            aggregateLimit: 0,
            loading: false,
            error: null,
          });
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        // toast.error("Failed to load data. Please try again.");
        setLimits({
          singleLimit: 0,
          aggregateLimit: 0,
          loading: false,
          error: null,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchLawFirmUserDetails();
  }, [id, location_id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleGiaFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setGiaFile(file);
      setFormData((prevData) => ({
        ...prevData,
        gia_document_name: file.name,
      }));
    }
  };

  const handleLoaFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLoaFile(file);
      setFormData((prevData) => ({
        ...prevData,
        loa_document_name: file.name,
      }));
    }
  };

  const handleLocationChange = (event) => {
    setSelectedLocation(event.target.value);
  };

  // New handler for law firm change
  const handleLawFirmChange = async (event) => {
    const newLawFirmId = event.target.value;
    setSelectedLawFirm(newLawFirmId);
    setSelectedLocation(""); // Reset location selection
    await fetchLocationsByLawFirm(newLawFirmId);
  };

  const handleCancel = () => {
    setFormData(initialData);
    setGiaFile(null);
    setLoaFile(null);
    if (responseGlobal) {
      const {
        lawFirmId: originalLawFirmId,
        locationId,
        shouldFetchLocations,
      } = resolveLawFirmUserResetSelection(responseGlobal, location_id);

      setSelectedLawFirm(originalLawFirmId);
      setSelectedLocation(locationId);

      if (shouldFetchLocations) {
        fetchLocationsByLawFirm(originalLawFirmId);
      }
    }
  };

  const buildPreferredName = (formData) => {
    const { prefix, firstName, middleName, lastName, suffix } = formData;
    return [prefix, firstName, middleName, lastName, suffix]
      .filter((name) => name !== undefined && name !== null && name !== "")
      .join(" ");
  };

  const handleResendInvite = async () => {
    setLoadingResend(true);
    try {
      const response = await apiClient(
        `/resend-lawfirm-user-invite/${id}`,
        "PATCH"
      );
      if (response) {
        toast.success("Lawfirm User invite Resent successfully");
      }
    } catch (error) {
      console.error("Error resending invite:", error);
      toast.error("Error updating user: " + (error.message || "Unknown error"));
    } finally {
      setLoadingResend(false);
    }
  };

  const handleSave = async () => {
    if (!formData) return;
    setSaving(true);

    try {
      // First upload GIA document if a new file was selected
      let gia_document_link = formData?.gia_document_link;
      let gia_document_name = formData?.gia_document_name;
      let loa_document_link = formData?.loa_document_link;
      let loa_document_name = formData?.loa_document_name;

      if (
        formData.position === "Attorney" &&
        giaFile &&
        formData.is_gia_on_file
      ) {
        const uploadResponse = await uploadGIA(giaFile);
        gia_document_link = uploadResponse.file_url;
        gia_document_name = uploadResponse.file_name;
      }

      if (
        formData.position === "Attorney" &&
        loaFile &&
        formData.is_loa_on_file
      ) {
        const uploadResponse = await uploadLOA(loaFile); // reuse same uploadGIA API
        loa_document_link = uploadResponse.file_url;
        loa_document_name = uploadResponse.file_name;
      }

      const dataToUpdate = {
        title: formData.title,
        law_firm_location_id: selectedLocation,
        position: formData.position,
        middle_name: formData.middleName,
        lawfirm_id: selectedLawFirm, // Use selected law firm instead of lawfirmId
        suffix: formData.suffix,
        prefix: formData.prefix,
        preferred_name: buildPreferredName(formData),
        extension: formData.extension,
        phone_number: formData.phoneNumber,
        is_default: formData.isDefault,
        is_legal_service: formData.isLegalService,
        is_active: formData.isActive,
        is_admin: formData.isAdmin,
        lawschool: formData.lawSchool,
        bar: formData.barNumber,
        is_gia_on_file: formData.is_gia_on_file,
        gia_document_link: gia_document_link,
        gia_document_name: gia_document_name,
        is_loa_on_file: formData.is_loa_on_file,
        loa_document_link: loa_document_link,
        loa_document_name: loa_document_name,
        firm_email: formData.useFirmEmail ? formData.firmEmail : null,
        user: {
          email: formData.email,
          first_name: formData.firstName,
          last_name: formData.lastName,
        },
      };
      await apiClient(`/lawfirm-user/${id}/${location_id}`, "PUT", dataToUpdate);
      toast.success("Lawfirm User details updated successfully!");
    } catch (error) {
      console.error("Error saving Lawfirm User details:", error);
      toast.error("Failed to save Lawfirm User details. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Format currency without decimals
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getBreakdown = () => {
    const direct = responseGlobal?.bond_totals_breakdown?.direct_lawfirm_user_link;
    const participant = responseGlobal?.bond_totals_breakdown?.participant_attorney_link;
    return {
      direct: {
        total_bonds: Number(direct?.total_bonds ?? formData?.total_bonds ?? 0),
        total_outstanding_bond_amount: Number(
          direct?.total_outstanding_bond_amount ?? formData?.total_bond_amount ?? 0
        ),
      },
      participant: {
        total_bonds: Number(participant?.total_bonds ?? 0),
        total_outstanding_bond_amount: Number(participant?.total_outstanding_bond_amount ?? 0),
      },
      hasBreakdown: !!(direct || participant),
    };
  };

  const BreakdownBlock = ({ label, value }) => (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: 0.2 }}>
        {value}
      </Typography>
    </Box>
  );

  const statCardSx = {
    height: "100%",
    borderRadius: 2,
    border: "1px solid",
    borderColor: "rgba(2, 6, 23, 0.08)",
    boxShadow: "0px 10px 24px rgba(2, 6, 23, 0.06)",
  };

  const statCardContentSx = { p: 2.5 };

  const StatTitle = ({ children }) => (
    <Typography
      variant="subtitle2"
      sx={{ color: "text.secondary", fontWeight: 700, letterSpacing: 0.2 }}
    >
      {children}
    </Typography>
  );

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ marginTop: 4, textAlign: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!formData) {
    return (
      <Container maxWidth="md" sx={{ marginTop: 4 }}>
        <Typography variant="h6" align="center">
          Failed to load data.
        </Typography>
      </Container>
    );
  }

  return (
    <>
      <Navbar />
      {formData.position === "Attorney" && (role === "super_user" || role === "internal_user") && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2, mr: 3 }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<VisibilityIcon />}
            sx={{ marginRight: "10px" }}
            onClick={() => navigate(`/attorney-bonds/${id}`)}
          >
            View Bonds
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate(`/lawfirm-user-limits/${id}`)} // Navigate to the new limits page
          >
            Manage Limits
          </Button>
        </Box>
      )}
      <Container maxWidth="xl" sx={{ marginTop: 2 }}>
        <Paper
          sx={{ padding: 4, backgroundColor: "#f5f5f5", borderRadius: "15px" }}
        >
          {/* User Header Section */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
              {formData.firstName} {formData.lastName}
            </Typography>
            <Typography
              variant="subtitle1"
              color="text.secondary"
            >
              {formData.position} • {formData.email}
            </Typography>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              sx={{
                ...(responseGlobal?.is_accepted ? {} : { color: "warning.main" }),
              }}
            >
              Registration Complete: {responseGlobal?.is_accepted ? "Yes" : "No"}
            </Typography>
            {responseGlobal?.is_accepted && (
            <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
              Username: {responseGlobal?.user?.username || "—"} • Last Logged In:{" "}
              {formatDateTime(responseGlobal?.user?.last_2fa_verified)}
            </Typography>
            )}

            {/* Stats Cards - Only show for Attorneys */}
            {formData.position === "Attorney" && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {/* Bond Stats Cards */}
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Card sx={statCardSx}>
                    <CardContent sx={statCardContentSx}>
                      <StatTitle>Total Bonds</StatTitle>
                      {(() => {
                        const { direct, participant, hasBreakdown } = getBreakdown();
                        if (!hasBreakdown) {
                          return (
                            <Typography variant="h3" sx={{ fontWeight: 800, mt: 1 }}>
                        {formData.total_bonds}
                      </Typography>
                          );
                        }
                        return (
                          <Box sx={{ mt: 1.5 }}>
                            <BreakdownBlock label="As Attorney" value={direct.total_bonds} />
                            <Divider sx={{ my: 1.25, borderColor: "rgba(2, 6, 23, 0.08)" }} />
                            <BreakdownBlock label="As Principal" value={participant.total_bonds} />
                          </Box>
                        );
                      })()}
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Card sx={statCardSx}>
                    <CardContent sx={statCardContentSx}>
                      <StatTitle>Total Bond Amount</StatTitle>
                      {(() => {
                        const { direct, participant, hasBreakdown } = getBreakdown();
                        if (!hasBreakdown) {
                          return (
                            <Typography variant="h3" sx={{ fontWeight: 800, mt: 1 }}>
                        {formatCurrency(formData.total_bond_amount)}
                      </Typography>
                          );
                        }
                        return (
                          <Box sx={{ mt: 1.5 }}>
                            <BreakdownBlock
                              label="As Attorney"
                              value={formatCurrency(direct.total_outstanding_bond_amount)}
                            />
                            <Divider sx={{ my: 1.25, borderColor: "rgba(2, 6, 23, 0.08)" }} />
                            <BreakdownBlock
                              label="As Principal"
                              value={formatCurrency(participant.total_outstanding_bond_amount)}
                            />
                          </Box>
                        );
                      })()}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Limits (combined) */}
                <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                  <Card sx={statCardSx}>
                    <CardContent sx={statCardContentSx}>
                      <StatTitle>Limits</StatTitle>
                      {limits.loading ? (
                        <Box sx={{ mt: 1.5 }}>
                        <CircularProgress size={24} />
                        </Box>
                      ) : limits.error ? (
                        <Typography color="error" sx={{ mt: 1.5 }}>
                          Error loading limits
                        </Typography>
                      ) : (
                        <Box sx={{ mt: 1.5 }}>
                          <BreakdownBlock
                            label="Single Limit"
                            value={formatCurrency(limits.singleLimit)}
                          />
                          <Divider sx={{ my: 1.25, borderColor: "rgba(2, 6, 23, 0.08)" }} />
                          <BreakdownBlock
                            label="Aggregate Limit"
                            value={formatCurrency(limits.aggregateLimit)}
                          />
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
          </Box>

          <Typography variant="h5" sx={{ marginBottom: 2 }}>
            Edit User Details
          </Typography>

          {/* Law Firm Selection */}
          <Box sx={{ marginBottom: 3 }}>
            <Typography variant="h6">1. Select Law Firm</Typography>
            <Select
              fullWidth
              value={selectedLawFirm}
              onChange={handleLawFirmChange}
              displayEmpty
              disabled={loadingLawFirms}
              sx={{ mt: 1 }}
            >
              <MenuItem value="" disabled>
                {loadingLawFirms ? "Loading law firms..." : "Select law firm"}
              </MenuItem>
              {lawFirms.map((firm) => (
                <MenuItem
                  key={firm.value}
                  value={firm.value}
                >
                  {firm.label}
                </MenuItem>
              ))}
            </Select>
          </Box>

          {/* Law Firm Location Selection */}
          <Box sx={{ marginBottom: 3 }}>
            <Typography variant="h6">2. Select Law Firm Location</Typography>
            <Select
              fullWidth
              value={selectedLocation}
              onChange={handleLocationChange}
              displayEmpty
              disabled={loadingLocations || !selectedLawFirm}
              sx={{ mt: 1 }}
            >
              <MenuItem value="" disabled>
                {loadingLocations 
                  ? "Loading locations..." 
                  : !selectedLawFirm 
                    ? "Please select a law firm first"
                    : "Select location"
                }
              </MenuItem>
              {locations.map((location) => (
                <MenuItem
                  key={location.value}
                  value={location.value}
                >
                  {location.label}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Box>
          <Typography variant="h6" sx={{ marginBottom: 2 }}>3. User Details</Typography>
            <Grid container spacing={2}>
              {/* Name Fields */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Prefix"
                  name="prefix"
                  fullWidth
                  value={formData.prefix}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="First Name"
                  name="firstName"
                  fullWidth
                  required
                  value={formData.firstName}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Middle Name"
                  name="middleName"
                  fullWidth
                  value={formData.middleName}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Last Name"
                  name="lastName"
                  fullWidth
                  required
                  value={formData.lastName}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Suffix"
                  name="suffix"
                  fullWidth
                  select
                  value={formData.suffix || ""}
                  onChange={handleInputChange}
                >
                  <MenuItem value="">Select suffix</MenuItem>
                  {suffixOptions.map((suffix) => (
                    <MenuItem key={suffix} value={suffix}>
                      {suffix}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Position and Permission Level */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Position"
                  name="position"
                  disabled
                  fullWidth
                  select
                  value={formData.position}
                  onChange={handleInputChange}
                >
                  {positions.map((position) => (
                    <MenuItem key={position} value={position}>
                      {position}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Permission Level"
                  name="isAdmin"
                  fullWidth
                  select
                  value={formData.isAdmin ? "Yes" : "No"}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      isAdmin: e.target.value === "Yes",
                    }))
                  }
                >
                  <MenuItem value="Yes">Admin</MenuItem>
                  <MenuItem value="No">User</MenuItem>
                </TextField>
              </Grid>

              {/* Attorney-specific fields */}
              {formData.position === "Attorney" && (
                <>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Autocomplete
                      options={lawSchools}
                      getOptionLabel={(option) => option.name || ""}
                      value={
                        lawSchools.find(
                          (school) => school.lawschool_id === formData.lawSchool
                        ) || null
                      }
                      onChange={(event, newValue) =>
                        handleInputChange({
                          target: {
                            name: "lawSchool",
                            value: newValue ? newValue.lawschool_id : "",
                          },
                        })
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Law School"
                          variant="outlined"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="Bar Number"
                      name="barNumber"
                      fullWidth
                      value={formData.barNumber}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="Is Default"
                      name="isDefault"
                      fullWidth
                      select
                      value={formData.isDefault ? "Yes" : "No"}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          isDefault: e.target.value === "Yes",
                        }))
                      }
                    >
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </TextField>
                  </Grid>
                </>
              )}

              {/* Paralegal-specific field */}
              {formData.position === "Paralegal" && (
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="Is this individual an Independent Paralegal who works for multiple law firms?"
                    name="isLegalService"
                    fullWidth
                    select
                    value={formData.isLegalService ? "Yes" : "No"}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        isLegalService: e.target.value === "Yes",
                      }))
                    }
                    disabled={role !== "internal_user" && role !== "super_user"}
                  >
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </TextField>
                </Grid>
              )}

              {/* Contact Information */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Email"
                  name="email"
                  fullWidth
                  required
                  value={formData.email}
                  onChange={(e) => {
                    handleInputChange(e);
                    debouncedCheckEmail(e.target.value);
                  }}
                  error={Boolean(emailExists)}
                  helperText={
                    emailChecking
                      ? "Checking email..."
                      : emailExists
                      ? "This email already exists."
                      : ""
                  }
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.useFirmEmail || false}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          useFirmEmail: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Use a different email for this firm's communications"
                />
                {formData.useFirmEmail && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="Firm-Specific Email"
                      name="firmEmail"
                      fullWidth
                      value={formData.firmEmail || ""}
                      onChange={handleInputChange}
                      helperText="This email will be used for communications related to this firm only. Login will still use the primary email above."
                    />
                  </Grid>
                )}
                {/* Email Exists Dialog */}
                <Dialog
                  open={emailDialogOpen}
                  onClose={(_, reason) => {
                    if (reason !== "backdropClick") handleDialogClose();
                  }}
                >
                  <DialogTitle>Email Already Exists</DialogTitle>
                  <DialogContent>
                    <DialogContentText>
                      This email already exists. Would you like to continue with this email?
                    </DialogContentText>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={handleDialogCancel} color="secondary">Cancel</Button>
                    <Button onClick={handleDialogContinue} color="primary" autoFocus>
                      Continue
                    </Button>
                  </DialogActions>
                </Dialog>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <InputMask
                  mask="(999) 999-9999"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                >
                  {(inputProps) => (
                    <TextField
                      {...inputProps}
                      label="Phone Number"
                      name="phoneNumber"
                      fullWidth
                    />
                  )}
                </InputMask>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Extension"
                  name="extension"
                  fullWidth
                  value={formData.extension}
                  onChange={handleInputChange}
                />
              </Grid>
            </Grid>

            {/* GIA Section */}
            {(formData.position === "Attorney") && (role === "super_user" || role === "internal_user")  && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                }}
              >
                <Typography variant="h6" sx={{ mb: 2 }}>
                  GIA Document
                </Typography>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.is_gia_on_file}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          is_gia_on_file: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Is GIA on file?"
                />

                {formData.is_gia_on_file && (
                  <>
                    {formData.gia_document_link ? (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body1">
                          Existing GIA Document:
                          <Link
                            href={formData.gia_document_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ ml: 1 }}
                          >
                            {formData.gia_document_name || "Download GIA"}
                          </Link>
                        </Typography>
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ mb: 2 }}
                      >
                        No GIA document uploaded yet.
                      </Typography>
                    )}

                    <Box>
                      <input
                        accept=".pdf,.doc,.docx"
                        style={{ display: "none" }}
                        id="gia-upload"
                        type="file"
                        onChange={handleGiaFileChange}
                      />
                      <label htmlFor="gia-upload">
                        <Button variant="contained" component="span">
                          {giaFile
                            ? "Change GIA Document"
                            : "Upload New GIA Document"}
                        </Button>
                      </label>
                      {giaFile && (
                        <Typography
                          variant="body2"
                          sx={{ ml: 2, display: "inline" }}
                        >
                          {formData.gia_document_name}
                        </Typography>
                      )}
                    </Box>
                  </>
                )}
              </Box>
            )}

            {/* LOA Section */}
            {(formData.position === "Attorney") && (role === "super_user" || role === "internal_user")  && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                }}
              >
                <Typography variant="h6" sx={{ mb: 2 }}>
                  LOA Document
                </Typography>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.is_loa_on_file}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          is_loa_on_file: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Is LOA on file?"
                />

                {formData.is_loa_on_file && (
                  <>
                    {formData.loa_document_link ? (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body1">
                          Existing LOA Document:
                          <Link
                            href={formData.loa_document_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ ml: 1 }}
                          >
                            {formData.loa_document_name || "Download LOA"}
                          </Link>
                        </Typography>
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ mb: 2 }}
                      >
                        No LOA document uploaded yet.
                      </Typography>
                    )}

                    <Box>
                      <input
                        accept=".pdf,.doc,.docx"
                        style={{ display: "none" }}
                        id="loa-upload"
                        type="file"
                        onChange={handleLoaFileChange}
                      />
                      <label htmlFor="loa-upload">
                        <Button variant="contained" component="span">
                          {loaFile
                            ? "Change LOA Document"
                            : "Upload New LOA Document"}
                        </Button>
                      </label>
                      {loaFile && (
                        <Typography
                          variant="body2"
                          sx={{ ml: 2, display: "inline" }}
                        >
                          {formData.loa_document_name}
                        </Typography>
                      )}
                    </Box>
                  </>
                )}
              </Box>
            )}

            {/* Save and Cancel Buttons */}
            <Box sx={{ mt: 4, textAlign: "right" }}>
              <Button onClick={handleCancel} color="secondary" sx={{ mr: 2 }}>
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                variant="contained"
                color="primary"
                disabled={saving || !selectedLawFirm || !selectedLocation}
              >
                {saving ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Save"
                )}
              </Button>
              {responseGlobal && (
                <Button
                  variant="contained"
                  color="secondary"
                  disabled={
                    loadingResend || responseGlobal.is_accepted === true
                  }
                  onClick={handleResendInvite}
                  style={{ marginLeft: "1rem" }}
                  startIcon={
                    loadingResend ? <CircularProgress size={24} /> : null
                  }
                >
                  {loadingResend ? "Resending..." : "Resend Invite"}
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      </Container>
    </>
  );
}

export default EditLawfirmUserDetailsForm;
